import { connectionHref, connectionSession } from "../connections/navigation";
/**
 * 能力：this machine's five registries, and when each entry last changed.
 *
 * There is no separate idea of "what is installed" anywhere in this system --
 * a channel, a tool, a skill, a model and a panel are all registry entries, and
 * their history is one kind of event. So this page is a reading of the
 * registries and of the `registry` writes on the tail, and nothing here keeps a
 * list of its own.
 *
 * There is no 「where to change what is installed」 section: the registries do not
 * say which plugin was granted what, so the server sends none
 * (`workbench/server/capabilities.py`), and a heading with nothing under it
 * said less than its absence.
 */

import { useCallback, useEffect, useState } from "react";
import {
  api,
  type Capabilities,
  type CapabilityEntry,
  type CapabilityRegistry,
  type ChannelStatus,
} from "../lib/api";
import { Clip, EmptyState, ErrorState, LoadingState, Scroller, Section } from "../shell/ui";
import { formatTime } from "../lib/format";

const KIND_LABEL: Record<string, string> = {
  channels: "渠道",
  tools: "工具",
  skills: "技能",
  models: "模型",
  ui_panels: "面板",
};

const KIND_HINT: Record<string, string> = {
  channels: "消息从哪进来、回哪去",
  tools: "模型和面板按钮能调什么",
  skills: "会做的事，正文不在核里",
  models: "拿什么想事情，以及单价",
  ui_panels: "插件自己声明的界面",
};

const ACTION_LABEL: Record<string, string> = {
  register: "注册",
  replace: "换代",
  revoke: "撤销",
};

export function CapabilitiesPage({ version }: { version: number }) {
  const [body, setBody] = useState<Capabilities | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(() => {
    api
      .capabilities()
      .then((next) => {
        setBody(next);
        setError(null);
      })
      .catch(setError);
  }, []);

  useEffect(load, [load, version]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!body) return <LoadingState label="读注册表" />;

  const total = body.registries.reduce((n, r) => n + r.entries.length, 0);

  return (
    <div className="capabilities" data-testid="capabilities-page">
      <header className="page-head">
        <h1 className="page-title" tabIndex={-1}>
          能力
        </h1>
        <p className="page-note micro">
          五本注册表，共 <span className="num">{total}</span> 条。装一个插件就是往这里写几行；
          撤一个就是撤掉它写的那几行。
        </p>
      </header>

      {body.registries.map((registry) => (
        <RegistrySection key={registry.kind} registry={registry} />
      ))}

      <Section title="注册史" meta={body.history.length ? `${body.history.length} 行` : undefined}>
        {body.history.length ? (
          <Scroller label="注册史">
            <table className="table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>动作</th>
                  <th>注册表</th>
                  <th>键</th>
                  <th>插件</th>
                  <th>代次</th>
                </tr>
              </thead>
              <tbody>
                {body.history.map((row) => (
                  <tr key={row.id}>
                    <td className="mono">{formatTime(row.ts)}</td>
                    <td>{ACTION_LABEL[row.action] ?? row.action}</td>
                    <td>{KIND_LABEL[row.kind] ?? row.kind}</td>
                    <td className="mono">
                      <Clip text={row.key} width="28ch" />
                    </td>
                    <td className="mono">{row.plugin ?? "未知"}</td>
                    <td className="mono">{row.generation_id ?? "未知"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Scroller>
        ) : (
          <EmptyState title="还没有看到注册史" hint="它随账本回放而来：核每写一次注册表，这里多一行。" />
        )}
      </Section>
    </div>
  );
}

function RegistrySection({ registry }: { registry: CapabilityRegistry }) {
  const label = KIND_LABEL[registry.kind] ?? registry.kind;
  // One asked-for answer per channel, kept here rather than in the row so a
  // re-render of the list does not throw them away.
  const [answers, setAnswers] = useState<Record<string, ChannelStatus | string>>({});
  const [asking, setAsking] = useState<string | null>(null);

  const ask = async (channel: string) => {
    setAsking(channel);
    try {
      const answer = await api.channelStatus(channel);
      setAnswers((prev) => ({ ...prev, [channel]: answer }));
    } catch (err) {
      setAnswers((prev) => ({ ...prev, [channel]: (err as Error).message }));
    } finally {
      setAsking(null);
    }
  };

  return (
    <Section title={label} meta={`${KIND_HINT[registry.kind] ?? registry.kind} · ${registry.entries.length}`}>
      {registry.problem ? (
        // Unreadable is not empty, and drawing an empty list for it would be a
        // claim that nothing is installed.
        <p className="form-error" role="alert">
          {registry.problem}
        </p>
      ) : registry.entries.length ? (
        <ul className="cap-list" data-testid={`cap-${registry.kind}`}>
          {registry.entries.map((entry) => (
            <CapabilityRow
              key={entry.key}
              entry={entry}
              answer={answers[entry.key]}
              asking={asking === entry.key}
              onAsk={() => void ask(entry.key)}
            />
          ))}
        </ul>
      ) : (
        <EmptyState title={`一条 ${label} 也没有`} hint="没有插件往这本注册表里写过东西。" />
      )}
    </Section>
  );
}

function CapabilityRow({
  entry,
  answer,
  asking,
  onAsk,
}: {
  entry: CapabilityEntry;
  answer?: ChannelStatus | string;
  asking?: boolean;
  onAsk?: () => void;
}) {
  return (
    <li className="cap-row">
      <div className="cap-row-head">
        <code className="mono cap-key">{entry.key}</code>
        <span className="micro">
          由 <code className="mono">{entry.plugin}</code> 注册
        </span>
        {/* The generation is what this page is for (§7.81: 换到第几代), so it
            is shown rather than hidden -- but a core is free to mint long ids,
            and one of those must not push the row's own name off the line. */}
        <span className="micro faint cap-gen">
          <Clip
            text={
              entry.spec_hash
                ? `${entry.generation_id ?? "未知"} · 声明摘要 ${entry.spec_hash}`
                : (entry.generation_id ?? "未知")
            }
            width="16ch"
          >
            {entry.generation_id ?? "未知"}
          </Clip>
        </span>
      </div>
      {entry.title && entry.title !== entry.key ? (
        <div className="cap-title">{entry.title}</div>
      ) : null}
      {entry.description ? <p className="micro">{entry.description}</p> : null}
      {/* A declaration this shell cannot draw (§7.143). Registered, so the row
          is here; unreadable, so the row says which field and why rather than
          leaving a nav item that opens an empty page. */}
      {entry.problem ? (
        <p className="form-error" role="alert">
          这块面板画不出来：{entry.problem}
        </p>
      ) : null}
      {entry.kind === "channels" ? (
        <ChannelState entry={entry} answer={answer} asking={asking} onAsk={onAsk} />
      ) : null}
    </li>
  );
}

/**
 * The two things a channel row has to say beyond "it is registered" (§7.116).
 *
 * They are separate because they fail separately. `needs_config` is the host's
 * finding at registration time -- keys the plugin asked for and did not get --
 * and it is fixed on that plugin's own settings panel, which is why the line
 * links there rather than to 设置 in general. The status is the channel's own
 * answer right now, and a channel can have every key it asked for and still be
 * unusable: WeChat needs somebody to scan a code.
 *
 * The answer is printed as it came back, with the plugin's own word for its
 * state in a code span. The workbench does not colour it: mapping a state
 * vocabulary it has never seen onto its own green and red would be inventing a
 * meaning, and it is the plugin that knows what 「blocked」 costs.
 */
export function ChannelState({
  entry,
  answer,
  asking,
  onAsk,
}: {
  entry: CapabilityEntry;
  answer?: ChannelStatus | string;
  asking?: boolean;
  onAsk?: () => void;
}) {
  const missing = entry.needs_config ?? [];
  return (
    <div className="cap-channel" data-testid={`cap-channel-${entry.key}`}>
      <div className="cap-channel-line">
        <span className="micro faint cap-channel-label">配置</span>
        {missing.length ? (
          <span className="micro">
            未配置：缺 <code className="mono">{missing.join("、")}</code>
            {entry.settings_panel ? (
              <>
                {" · "}
                <a
                  href={connectionHref(entry.plugin, connectionSession(location.hash, "") || undefined)}
                  data-testid={`cap-channel-fix-${entry.key}`}
                >
                  配置这个渠道
                </a>
              </>
            ) : (
              <>{" · 这个插件没有声明设置面板，只能改核的配置文件。"}</>
            )}
          </span>
        ) : (
          <span className="micro">宿主注册它的时候，它要的配置都齐了。</span>
        )}
      </div>
      <div className="cap-channel-line">
        <span className="micro faint cap-channel-label">现在能不能用</span>
        {entry.status_tool ? (
          <>
            <button
              type="button"
              className="btn"
              disabled={asking}
              data-testid={`cap-channel-ask-${entry.key}`}
              onClick={onAsk}
            >
              {asking ? "问着" : `问一次 ${entry.status_tool}`}
            </button>
            {answer === undefined ? (
              <span className="micro faint">「已配置」不等于「能用」，按一下才知道。</span>
            ) : typeof answer === "string" ? (
              <span className="micro" role="alert">
                问不到：{answer}
              </span>
            ) : (
              <span className="micro">
                {answer.result?.state ? (
                  <code className="mono">{answer.result.state}</code>
                ) : null}{" "}
                {answer.result?.summary ?? "渠道没有说它怎么样。"}
              </span>
            )}
          </>
        ) : (
          <span className="micro faint">
            没有注册 <code className="mono">{entry.key}.status</code>，问不了。
          </span>
        )}
      </div>
    </div>
  );
}
